import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { learningPlansService, UserDashboardStats } from '../services/firebase/learningPlans';

interface UseDashboardStatsOptions {
  /**
   * Whether to subscribe to Firestore for real-time updates
   */
  subscribe?: boolean;
  /**
   * Whether to automatically load stats when the hook mounts
   */
  autoRefresh?: boolean;
}

interface UseDashboardStatsResult {
  stats: UserDashboardStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Shared hook for loading and optionally subscribing to dashboard statistics
 */
export const useDashboardStats = (
  options: UseDashboardStatsOptions = {}
): UseDashboardStatsResult => {
  const { subscribe = false, autoRefresh = true } = options;
  const { user } = useAuth();

  const [stats, setStats] = useState<UserDashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(autoRefresh);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      if (isMountedRef.current) {
        setStats(null);
        setLoading(false);
        setError(null);
      }
      return;
    }

    if (isMountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      await learningPlansService.calculateDashboardStats();
      const latestStats = await learningPlansService.getDashboardStats();

      if (isMountedRef.current) {
        setStats(latestStats);
      }
    } catch (err) {
      console.error('Error loading dashboard stats:', err);

      if (isMountedRef.current) {
        setError('Failed to load dashboard stats');
      }

      throw err instanceof Error ? err : new Error('Failed to load dashboard stats');
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [user]);

  useEffect(() => {
    if (!autoRefresh) return;
    refresh();
  }, [autoRefresh, refresh]);

  useEffect(() => {
    if (!subscribe || !user) {
      return;
    }

    const unsubscribe = learningPlansService.subscribeToDashboardStats(nextStats => {
      if (!isMountedRef.current) return;

      setStats(nextStats);
      setLoading(false);

      if (nextStats) {
        setError(null);
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [subscribe, user]);

  useEffect(() => {
    if (!user && isMountedRef.current) {
      setStats(null);
      setLoading(false);
      setError(null);
    }
  }, [user]);

  return {
    stats,
    loading,
    error,
    refresh,
  };
};

export default useDashboardStats;
